import React, { useEffect, useState } from 'react';
import { X, RotateCcw } from 'lucide-react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { CatalogFilters, DEFAULT_FILTERS } from '../hooks/useFilms';
interface FilterSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  filters: CatalogFilters;
  onApply: (filters: CatalogFilters) => void;
  className?: string;
}
const GENRE_OPTIONS = ['драма', 'комедия', 'мелодрама', 'военный', 'фантастика', 'приключения', 'детектив', 'триллер', 'боевик', 'мультфильм', 'документальный', 'история', 'биография', 'семейный', 'музыка', 'криминал', 'фэнтези', 'ужасы', 'вестерн', 'спорт'];
const AGE_OPTIONS = ['0+', '6+', '12+', '16+', '18+'];
const STUDIO_OPTIONS = ['Мосфильм', 'Ленфильм', 'Киностудия им. Горького', 'Одесская киностудия', 'Грузия-фильм', 'Беларусьфильм', 'Казахфильм', 'Узбекфильм', 'Таллинфильм', 'Рижская киностудия'];
export function FilterSidebar({
  isOpen,
  onClose,
  filters,
  onApply,
  className = ''
}: FilterSidebarProps) {
  // Ensure filters always has valid defaults
  const safeFilters = filters ?? DEFAULT_FILTERS;
  // Local draft state so user can tweak before applying
  const [draft, setDraft] = useState<CatalogFilters>(safeFilters);
  // Sync draft when external filters change
  useEffect(() => {
    setDraft(filters ?? DEFAULT_FILTERS);
  }, [filters]);
  const toggleGenre = (genre: string) => {
    setDraft(prev => ({
      ...prev,
      genres: prev.genres.includes(genre) ? prev.genres.filter(g => g !== genre) : [...prev.genres, genre]
    }));
  };
  const toggleAge = (age: string) => {
    setDraft(prev => ({
      ...prev,
      ageCategories: prev.ageCategories.includes(age) ? prev.ageCategories.filter(a => a !== age) : [...prev.ageCategories, age]
    }));
  };
  const toggleStudio = (studio: string) => {
    setDraft(prev => ({
      ...prev,
      studios: prev.studios.includes(studio) ? prev.studios.filter(s => s !== studio) : [...prev.studios, studio]
    }));
  };
  const handleApply = () => {
    onApply(draft);
    onClose();
  };
  const handleReset = () => {
    setDraft(DEFAULT_FILTERS);
    onApply(DEFAULT_FILTERS);
  };
  const hasActiveFilters = draft.genres.length > 0 || draft.ageCategories.length > 0 || draft.studios.length > 0 || draft.yearFrom > 1920 || draft.yearTo < 2000 || draft.search.trim() !== '';
  return <>
      {/* Overlay for mobile */}
      {isOpen && <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40 md:hidden" onClick={onClose} data-id="element-56" />}

      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-80 bg-[#0a0a0a] border-r border-[#222] transform transition-transform duration-500 ease-out overflow-y-auto
        md:static md:translate-x-0 md:h-auto md:z-0
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        ${className}
      `} data-id="element-57">
        <div className="p-6 space-y-8" data-id="element-58">
          <div className="flex items-center justify-between" data-id="element-59">
            <h2 className="font-noir text-xl text-white" data-id="element-60">Фильтры</h2>
            <div className="flex items-center gap-2" data-id="element-61">
              {hasActiveFilters && <button onClick={handleReset} className="p-2 hover:bg-[#222] rounded-full text-gray-500 hover:text-[#D22B2B] transition-colors" title="Сбросить фильтры" data-id="element-62">
                  <RotateCcw className="w-4 h-4" data-id="element-63" />
                </button>}
              <button onClick={onClose} className="p-2 hover:bg-[#222] rounded-full text-white md:hidden" data-id="element-64">
                <X className="w-5 h-5" data-id="element-65" />
              </button>
            </div>
          </div>

          {/* Search in Catalog */}
          <div className="space-y-3" data-id="element-66">
            <h3 className="font-serif text-xs font-bold uppercase tracking-[0.2em] text-[#D22B2B] border-b border-[#333] pb-2 inline-block" data-id="element-67">
              Поиск в каталоге
            </h3>
            <Input placeholder="Найти фильм..." value={draft.search} onChange={e => setDraft(prev => ({
            ...prev,
            search: e.target.value
          }))} className="h-10 text-sm bg-[#141414]" data-id="element-68" />
          </div>

          {/* Year Range */}
          <div className="space-y-3" data-id="element-69">
            <h3 className="font-serif text-xs font-bold uppercase tracking-[0.2em] text-[#D22B2B] border-b border-[#333] pb-2 inline-block" data-id="element-70">
              Год выпуска
            </h3>
            <div className="flex items-center gap-2 text-sm" data-id="element-71">
              <Input type="number" value={draft.yearFrom} onChange={e => setDraft(prev => ({
              ...prev,
              yearFrom: parseInt(e.target.value) || 1920
            }))} className="h-9 w-20 text-center bg-[#141414]" data-id="element-72" />
              <span className="text-gray-600" data-id="element-73">—</span>
              <Input type="number" value={draft.yearTo} onChange={e => setDraft(prev => ({
              ...prev,
              yearTo: parseInt(e.target.value) || 2000
            }))} className="h-9 w-20 text-center bg-[#141414]" data-id="element-74" />
            </div>
            <div className="space-y-2" data-id="element-75">
              <input type="range" min="1920" max="2000" value={draft.yearFrom} onChange={e => setDraft(prev => ({
              ...prev,
              yearFrom: Math.min(parseInt(e.target.value), prev.yearTo)
            }))} className="w-full accent-[#D22B2B] bg-[#333] h-1 rounded-full appearance-none cursor-pointer" data-id="element-76" />
              <input type="range" min="1920" max="2000" value={draft.yearTo} onChange={e => setDraft(prev => ({
              ...prev,
              yearTo: Math.max(parseInt(e.target.value), prev.yearFrom)
            }))} className="w-full accent-[#D22B2B] bg-[#333] h-1 rounded-full appearance-none cursor-pointer" data-id="element-77" />
            </div>
            <div className="flex justify-between text-[10px] text-gray-600 font-serif" data-id="element-78">
              <span data-id="element-79">{draft.yearFrom}</span>
              <span data-id="element-80">{draft.yearTo}</span>
            </div>
          </div>

          {/* Genres */}
          <div className="space-y-3" data-id="element-81">
            <h3 className="font-serif text-xs font-bold uppercase tracking-[0.2em] text-[#D22B2B] border-b border-[#333] pb-2 inline-block" data-id="element-82">
              Жанр
              {draft.genres.length > 0 && <span className="ml-2 text-gray-500" data-id="element-83">
                  ({draft.genres.length})
                </span>}
            </h3>
            <div className="space-y-2 max-h-64 overflow-y-auto pr-2" data-id="element-84">
              {GENRE_OPTIONS.map(genre => {
              const isChecked = draft.genres.includes(genre);
              return <label key={genre} className="flex items-center gap-3 cursor-pointer group" data-id="element-85">
                    <div className={`relative flex items-center justify-center w-4 h-4 border transition-colors bg-[#141414] ${isChecked ? 'border-[#D22B2B]' : 'border-[#444] group-hover:border-[#D22B2B]'}`} data-id="element-86">
                      <input type="checkbox" checked={isChecked} onChange={() => toggleGenre(genre)} className="peer appearance-none absolute inset-0 w-full h-full cursor-pointer" data-id="element-87" />
                      <div className={`w-2 h-2 bg-[#D22B2B] transition-opacity shadow-[0_0_5px_#D22B2B] ${isChecked ? 'opacity-100' : 'opacity-0'}`} data-id="element-88"></div>
                    </div>
                    <span className={`text-sm font-light capitalize transition-colors ${isChecked ? 'text-white' : 'text-gray-400 group-hover:text-white'}`} data-id="element-89">
                      {genre}
                    </span>
                  </label>;
            })}
            </div>
          </div>

          {/* Age Category */}
          <div className="space-y-3" data-id="element-90">
            <h3 className="font-serif text-xs font-bold uppercase tracking-[0.2em] text-[#D22B2B] border-b border-[#333] pb-2 inline-block" data-id="element-91">
              Возраст
              {draft.ageCategories.length > 0 && <span className="ml-2 text-gray-500" data-id="element-92">
                  ({draft.ageCategories.length})
                </span>}
            </h3>
            <div className="flex flex-wrap gap-2" data-id="element-93">
              {AGE_OPTIONS.map(age => {
              const isActive = draft.ageCategories.includes(age);
              return <button key={age} onClick={() => toggleAge(age)} className={`px-3 py-1.5 border text-[10px] font-bold transition-all duration-200 ${isActive ? 'border-[#D22B2B] text-[#D22B2B] bg-[#D22B2B]/10 shadow-[0_0_8px_rgba(210,43,43,0.2)]' : 'border-[#333] text-gray-500 hover:border-[#D22B2B] hover:text-[#D22B2B]'}`} data-id="element-94">
                    {age}
                  </button>;
            })}
            </div>
          </div>

          {/* Studio */}
          <div className="space-y-3" data-id="element-95">
            <h3 className="font-serif text-xs font-bold uppercase tracking-[0.2em] text-[#D22B2B] border-b border-[#333] pb-2 inline-block" data-id="element-96">
              Киностудия
              {draft.studios.length > 0 && <span className="ml-2 text-gray-500" data-id="element-97">
                  ({draft.studios.length})
                </span>}
            </h3>
            <div className="space-y-2 max-h-48 overflow-y-auto pr-2" data-id="element-98">
              {STUDIO_OPTIONS.map(studio => {
              const isChecked = draft.studios.includes(studio);
              return <label key={studio} className="flex items-center gap-3 cursor-pointer group" data-id="element-99">
                    <div className={`relative flex items-center justify-center w-4 h-4 border transition-colors bg-[#141414] ${isChecked ? 'border-[#D22B2B]' : 'border-[#444] group-hover:border-[#D22B2B]'}`} data-id="element-100">
                      <input type="checkbox" checked={isChecked} onChange={() => toggleStudio(studio)} className="peer appearance-none absolute inset-0 w-full h-full cursor-pointer" data-id="element-101" />
                      <div className={`w-2 h-2 bg-[#D22B2B] transition-opacity shadow-[0_0_5px_#D22B2B] ${isChecked ? 'opacity-100' : 'opacity-0'}`} data-id="element-102"></div>
                    </div>
                    <span className={`text-sm font-light transition-colors ${isChecked ? 'text-white' : 'text-gray-400 group-hover:text-white'}`} data-id="element-103">
                      {studio}
                    </span>
                  </label>;
            })}
            </div>
          </div>

          {/* Apply / Reset */}
          <div className="pt-4 border-t border-[#222] space-y-3" data-id="element-104">
            <Button className="w-full" onClick={handleApply} data-id="element-105">
              Применить
            </Button>
            {hasActiveFilters && <button onClick={handleReset} className="w-full text-xs font-serif uppercase tracking-widest text-gray-500 hover:text-[#D22B2B] transition-colors py-2" data-id="element-106">
                Сбросить все фильтры
              </button>}
          </div>
        </div>
      </aside>
    </>;
}